import { between, integer, maxValue, minValue, required } from 'vuelidate/lib/validators';
import { ILccaCalculatedImperial } from '@/modules/lcca/interfaces/lccaCalculated';
import { getProbabilisticByName, getUserProbInputs } from '@/modules/lcca/lib/util';
import { BaseProbVariables } from '@/modules/lcca/interfaces/probabilistic';
import { IDistribution } from '@/lib/monteCarlo/interfaces';

export const createValidatorsForDistribution = function (D: IDistribution) {
  // If there's no distribution then return no validators
  if (D === undefined || D === null) {
    return {};
  }

  // Otherwise ID the variables used and continue
  const varsUsed = variablesUsedInDistribution.filter((V) => V.prob === D.prob)[0];
  const varsToCheck = ['min', 'mean', 'mode', 'max', 'std', 'location', 'shape'];

  const distValidators: { [index: string]: any } = {};

  varsToCheck.forEach((varName) => {
    // @ts-ignore
    if (varsUsed[varName] === true) {
      if (varName !== 'std') {
        distValidators[varName] = {
          required,
        };
      } else if (varName === 'std') {
        distValidators[varName] = {
          required,
          minValue: minValue(0),
        };
      }
    } else {
      distValidators[varName] = {};
    }
  });
  return distValidators;
};

const variablesUsedInDistribution = [
  {
    text: 'Deterministic',
    prob: 'deterministic',
    min: false,
    mean: true,
    mode: false,
    max: false,
    std: false,

  },
  {
    text: 'Uniform',
    prob: 'uniform',

    min: true,
    mean: false,
    mode: false,
    max: true,
    std: false,
  },
  {
    text: 'Normal',
    prob: 'normal',
    min: false,
    mean: true,
    mode: false,
    max: false,
    std: true,
  },
  {
    text: 'Triangular',
    prob: 'triangular',
    min: true,
    mean: false,
    mode: true,
    max: true,
    std: false,
  },
  {
    text: 'LogNormal',
    prob: 'lognormal',
    min: true,
    mean: false,
    mode: false,
    max: true,
    std: false,
  },
  {
    text: 'Truncated Normal',
    prob: 'truncatednormal',
    min: true,
    mean: true,
    mode: false,
    max: true,
    std: true,

  },
  {
    text: 'Truncated Lognormal',
    prob: 'truncatedlognormal',
    min: true,
    mean: true,
    mode: false,
    max: true,
    std: true,
  },
];

export const createLccaValidations = (scenario: ILccaCalculatedImperial) => ({
  scenarioImperial: {
    name: {
      required,
    },
    analysisPeriodStart_yr: {
      required,
      minValue: minValue(new Date(Date.now()).getFullYear() - 100),
      maxValue: maxValue(new Date(Date.now()).getFullYear() + 100),
    },
    analysisPeriodLength_yrs: {
      required,
      minValue: minValue(1),
      maxValue: maxValue(100),
    },
    state: {
      required,
    },


    // Page 2

    trafficYear: {
      required,
      minValue: minValue(new Date(Date.now()).getFullYear() - 100),
      maxValue: maxValue(new Date(Date.now()).getFullYear() + 100),
    },

    singleTruckAadtPct: {
      required,
      minValue: minValue(0),
      maxValue: maxValue(100),

    }, // singleTruckAADT
    combinationTruckAadtPct: {
      required,
      minValue: minValue(0),
      maxValue: maxValue(100),

    }, // combinationTruckAADT
    numLanesNormal: {
      required,
      minValue: minValue(0),
    },

    aadtAnalysisPeriod: {
      required, minValue: minValue(0),
    },
    freeFlowCapacity: {
      required, minValue: minValue(0),
    }, // vphpl
    speedLimitNormal_mph: {
      required, minValue: minValue(0),
    },
    maxQueueLength_mi: {
      required, minValue: minValue(0),
    },
    noShowPct: {
      required, minValue: minValue(0),
    },
    detourPct: {
      required, minValue: minValue(0),
    },
    additionalDetourTime_min: {
      required, minValue: minValue(0),
    },

    // Page 3

    // Page 4


  },

  /*
  Top level validations - not on model directly
   */


  // Page 2

  trafficProbabilisticInputs: createValidatorsForDistribution(getProbabilisticByName(scenario.probabilistic, BaseProbVariables.annualGrowthTraffic)),

  // Page 3
  trafficDistTotal: {
    integer,
    between: between(0.5, 1.5),

  },

  // Page 4
  userProbInputs: (function () {
    const userProbInputs = getUserProbInputs(scenario);

    const probInputsValidators: { [index: string]: any } = {};

    Object.keys(userProbInputs).forEach((k) => {
      const D = userProbInputs[k];
      probInputsValidators[k] = createValidatorsForDistribution(D);
    });
    return probInputsValidators;
  }()),


  1: [
    'scenarioImperial.name',
    'scenarioImperial.analysisPeriodStart_yr',
    'scenarioImperial.analysisPeriodLength_yrs',
    'scenarioImperial.state',
  ],
  2: [

    'scenarioImperial.trafficYear',
    'scenarioImperial.singleTruckAadtPct',
    'scenarioImperial.combinationTruckAadtPct',
    'scenarioImperial.numLanesNormal',
    'scenarioImperial.aadtAnalysisPeriod',
    'scenarioImperial.freeFlowCapacity',
    'scenarioImperial.speedLimitNormal_mph',
    'scenarioImperial.maxQueueLength_mi',
    'scenarioImperial.noShowPct',
    'scenarioImperial.detourPct',
    'scenarioImperial.additionalDetourTime_min',
  ],
  3: ['trafficDistTotal'],
  4: ['userProbInputs'],
  5: [],
  6: [
    '1',
    '2',
    '3',
    '4',
    '5',
  ],


  // aadtGroupPorous: [
  //   'scenarioImperial.initialTraffic',
  //   'scenarioImperial.loadEquivalencyFactor',
  //   'scenarioImperial.esals',
  //   'scenarioImperial.trafficGrowthRate',
  //   'scenarioImperial.esalGrowthRate',
  //   'scenarioImperial.totalDesignEsals',
  //   'scenarioImperial.loadDiameter_in'
  // ],
  // annualEsalGroupPorous: [
  //   'scenarioImperial.esals',
  //   'scenarioImperial.esalGrowthRate',
  //   'scenarioImperial.totalDesignEsals',
  //   'scenarioImperial.loadDiameter_in'
  // ],
  // designEsalGroupPorous: [
  //   'scenarioImperial.totalDesignEsals',
  //   'scenarioImperial.loadDiameter_in'
  // ],

});
